import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { bibliographyColumnsVisibility } from "@/config/dashboard/bibliography";
import { multipleprintsLinksColumnsVisibility } from "@/config/dashboard/multipleprints";
import { FormLayoutElement } from "@/config/forms/general";
import { computeFieldMultipleprintRelatedAuthors } from "@/lib/computeFields";
import i18n, { getMainLanguage, SupportedLocale } from "@/i18n";
import {
  Author,
  Bibliography,
  Multipleprint,
  MultipleprintInBibliography,
} from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  BibliographyLinkGridCard,
  MultipleprintLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import MultipleprintInBibliographyDrawer from "./drawer/MultipleprintInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  multipleprint?: Multipleprint;
  bibliography?: Bibliography;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

/**
 * MultipleprintInBibliography component
 * @returns {JSX.Element}
 */
const MultipleprintInBibliographyLinks = ({
  id,
  multipleprint,
  bibliography,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "multipleprintInBibliography",
    type,
  );

  const { mutate: deleteMultipleprintInBibliography } = useDeleteComplexLink(
    id,
    "multipleprintInBibliography",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteMultipleprintInBibliography({
        id,
        relation: "multipleprintInBibliography",
      });
    },
    [deleteMultipleprintInBibliography],
  );

  const renderMultipleprintInBibliographyRowActions = useCallback(
    (data: MultipleprintInBibliography) => {
      const preview =
            type === "bibliography" && data.multipleprint
              ? {
                  previewType: "multipleprint" as const,
                  previewId: data.multipleprint.id,
                  previewElements: [
                    {
                      label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
                      value:
                        data.multipleprint.estampesMultiplesDescriptionNumeroEnregistrement,
                      type: "text" as const,
                    },
                    {
                      label: t("estampesMultiplesDescriptionResume"),
                      value: data.multipleprint.estampesMultiplesDescriptionResume,
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.multipleprint.attachments,
                      type: "image" as const,
                    },
                  ],
                }
              : type === "multipleprint" && data.bibliography
                ? {
                    previewType: "bibliography" as const,
                    previewId: data.bibliography.id,
                    previewElements: [
                      {
                        label: t("bibliographieDescriptionNumeroBibliographie"),
                        value:
                          data.bibliography.bibliographieDescriptionNumeroBibliographie,
                        type: "text" as const,
                      },
                      {
                        label: t("bibliographieDescriptionResumeLong"),
                        value: data.bibliography.bibliographieDescriptionResumeLong,
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value: data.bibliography.attachments,
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <MultipleprintInBibliographyDrawer
                  key={data.id}
                  fields={fields}
                  data={data}
                  queryId={id}
                  type={type}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<MultipleprintInBibliography, any>[] = useMemo(
    () => [
      ...(type === "bibliography"
        ? [
            {
              accessorKey: "vignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<MultipleprintInBibliography> }) => (
                <ImageCell
                  value=""
                  attachments={row.original.multipleprint?.attachments ?? []}
                  domain="multipleprint"
                />
              ),
            },
            {
              accessorKey: "estampesMultiplesDescriptionTitre",
              header: t("estampesMultiplesDescriptionTitre"),
              cell: (props: { getValue: () => unknown }) => (
                <TextCell value={String(props.getValue() ?? "")} />
              ),
            },
            {
              accessorKey: "author",
              header: t("tab_relatedAuthor"),
              cell: ({ row }: { row: Row<MultipleprintInBibliography> }) => {
                const mp = row.original.multipleprint;
                if (!mp) return null;
                return (
                  <TextCell
                    value={computeFieldMultipleprintRelatedAuthors(mp, {
                      language: i18n.language as SupportedLocale,
                    })}
                  />
                );
              },
            },
            {
              accessorKey: "estampesMultiplesDescriptionDateDeCreation",
              header: t("tab_estampesMultiplesDescriptionDateDeCreation"),
              cell: ({ row }: { row: Row<MultipleprintInBibliography> }) => (
                <TextCell
                  value={
                    row.original.multipleprint
                      ?.estampesMultiplesDescriptionDateDeCreation
                  }
                />
              ),
            },
            {
              accessorKey: "estampesMultiplesDescriptionResume",
              header: t("estampesMultiplesDescriptionResume"),
              cell: ({ row }: { row: Row<MultipleprintInBibliography> }) => (
                <HtmlCell
                  value={
                    row.original.multipleprint
                      ?.estampesMultiplesDescriptionResume ?? ""
                  }
                />
              ),
            }
          ]
        : [
            {
              accessorKey: "bibliographieDescriptionVignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<MultipleprintInBibliography> }) => {
                return (
                  <ImageCell
                    value={""}
                    attachments={row.original.bibliography?.attachments ?? []}
                    domain={"bibliography"}
                  />
                );
              },
            },
            {
              accessorKey: "bibliographieDescriptionAnnee",
              header: t("bibliographieDescriptionAnnee"),
              cell: ({ row }: { row: Row<MultipleprintInBibliography> }) => (
                <TextCell
                  value={row.original.bibliography?.bibliographieDescriptionAnnee ?? ""}
                />
              ),
            },
            {
              accessorKey: "bibliographieDescriptionAuteur",
              header: t("bibliographieDescriptionAuteur"),
              cell: ({ row }: { row: Row<MultipleprintInBibliography> }) => (
                <TextCell
                  value={
                    row.original.bibliography?.bibliographieDescriptionAuteur ?? ""
                  }
                />
              ),
            },
          ]),
      {
        accessorKey:
          "bibliographieDansEstampesMultiplesDescriptionReproduitPage",
        header: t("bibliographieDansEstampesMultiplesDescriptionReproduitPage"),
      },
      {
        accessorKey: "bibliographieDansEstampesMultiplesDescriptionCitePage",
        header: t("bibliographieDansEstampesMultiplesDescriptionCitePage"),
      },
      {
        accessorKey: "bibliographieDansEstampesMultiplesDescriptionPhotographe",
        header: t("bibliographieDansEstampesMultiplesDescriptionPhotographe"),
      },
      {
        accessorKey:
          "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
        header: t(
          "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
        ),
        cell: (props: { getValue: () => unknown }) => (
          <TextCell
            value={props.getValue() ? t("yes") : t("no")}
          />
        ),
      },
      {
        accessorKey:
          "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
        header: t(
          "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
        ),
      },
      createActionsColumnDef<MultipleprintInBibliography>(({ row }) =>
        renderMultipleprintInBibliographyRowActions(row.original),
      ),
    ],
    [fields, handleDeleteRelation, id, t, type],
  );

  return (
    <div className="w-full">
      {type === "bibliography" ? (
        <BibliographyToMultipleprintLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          bibliography={bibliography}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderMultipleprintInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedMultipleprints")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      ) : (
        <MultipleprintToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          multipleprint={multipleprint}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderMultipleprintInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedBibliographies")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      )}
    </div>
  );
};

type BibliographyToMultipleprintLinksProps =
  LinksTableProps<MultipleprintInBibliography> & {
    bibliography: Bibliography | undefined;
  };

type MultipleprintInBibliographyLinksProps =
  LinksTableProps<MultipleprintInBibliography> & {
    multipleprint: Multipleprint | undefined;
  };

/** Vue depuis une bibliographie : liste des multiples liés à cette biblio */
const BibliographyToMultipleprintLinks = ({
  id,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: BibliographyToMultipleprintLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "bibliography-links-multipleprint-add";
  const STORAGE_KEY_LINKED = "bibliography-links-multipleprint-linked";
  const defaultVisibilityAdd = { ...multipleprintsLinksColumnsVisibility, actions: true };
  const defaultVisibilityLinked = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityAdd, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityAdd };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityLinked, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityLinked };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedMultiples } = useGetNotRelatedResource<Multipleprint>({
    resultType: "multiple",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsMultiples: ColumnDef<Multipleprint, any>[] = useMemo(
    () => [
      {
        accessorKey: "estampesMultiplesDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Multipleprint> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="multipleprint"
          />
        ),
      },
      {
        accessorKey: "estampesMultiplesDescriptionTitre",
        header: t("estampesMultiplesDescriptionTitre"),
        cell: ({ row }: { row: Row<Multipleprint> }) => (
          <HtmlCell
            value={row.original.estampesMultiplesDescriptionTitre ?? ""}
          />
        ),
      },
      {
        id: "estampesMultiplesDescriptionTechnique",
        header: t("estampesMultiplesDescriptionTechnique"),
        accessorFn: (row: Multipleprint) =>
          row.estampesMultiplesDescriptionTechnique?.[
            getMainLanguage() as SupportedLocale
          ] ?? "",
        cell: ({ row }: { row: Row<Multipleprint> }) => (
          <TextCell
            value={
              row.original.estampesMultiplesDescriptionTechnique?.[
                getMainLanguage() as SupportedLocale
              ] ?? ""
            }
          />
        ),
      },
      createActionsColumnDef<Multipleprint>(({ row }: { row: Row<Multipleprint> }) => {
          const data = row.original as Multipleprint;
          if (!bibliography) return null;
          return (
            <LinkDrawerRowActions
              previewType="multipleprint"
              previewId={data.id}
              previewElements={[
                {
                  label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
                  value: data.estampesMultiplesDescriptionNumeroEnregistrement ?? "",
                  type: "text",
                },
                {
                  label: t("estampesMultiplesDescriptionResume"),
                  value: data.estampesMultiplesDescriptionResume ?? "",
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultipleprintInBibliographyDrawer
                  key={data.id}
                  fields={fields}
                  queryId={id}
                  type="bibliography"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography,
                    multipleprint: data,
                    bibliographieDansEstampesMultiplesDescriptionReproduitPage: "",
                    bibliographieDansEstampesMultiplesDescriptionReproduitPlanche:
                      "",
                    biblioInMultiPrintDescPlancheNonNum: false,
                    bibliographieDansEstampesMultiplesDescriptionCitePage: "",
                    bibliographieDansEstampesMultiplesDescriptionListePage: "",
                    bibliographieDansEstampesMultiplesDescriptionSousNumero: "",
                    bibliographieDansEstampesMultiplesDescriptionPhotographe: "",
                    bibliographieDansEstampesMultiplesDescriptionReproductionCoul: false,
                    biblioInMultiPrintDescReproCover: false,
                    bibliographieDansEstampesMultiplesDescriptionNonPagine: false,
                    bibliographieDansEstampesMultiplesDescriptionExemplaireRepr: "",
                    bibliographieDansEstampesMultiplesDescriptionTexteLibre: "",
                  }}
                />
              )}
            />
          );
        }),
    ],
    [bibliography, fields, id, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsMultiples}
      data={notRelatedMultiples?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notRelatedMultiples?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <MultipleprintLinkGridCard
                  item={row.multipleprint}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <MultipleprintInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="bibliography"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) {
                      setSelectedRelationId(null);
                    }
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

//Composant pour Oeuvres
const MultipleprintToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  multipleprint,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: MultipleprintInBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "multipleprint-links-bibliography-add";
  const STORAGE_KEY_LINKED = "multipleprint-links-bibliography-linked";
  const defaultVisibility = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resultType: "bibliography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionNumeroBibliographie}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-3 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionAnnee}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "relatedAuthor",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-3 text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.relatedAuthor
                  .map((author: Author) => author.artistesAuteursDescriptionNom)
                  .join(", ")}
              </p>
            </div>
          );
        },
      },
      createActionsColumnDef<Bibliography>(({ row }) => {
          const data = row.original as Bibliography;
          if (!multipleprint) return null;
          return (
            <LinkDrawerRowActions
              previewType="bibliography"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("bibliographieDescriptionNumeroBibliographie"),
                  value: data.bibliographieDescriptionNumeroBibliographie,
                  type: "text",
                },
                {
                  label: t("bibliographieDescriptionResumeLong"),
                  value: data.bibliographieDescriptionResumeLong,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultipleprintInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type={"multipleprint"}
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: data,
                    multipleprint: multipleprint,
                    bibliographieDansEstampesMultiplesDescriptionReproduitPage:
                      "",
                    bibliographieDansEstampesMultiplesDescriptionReproduitPlanche:
                      "",
                    biblioInMultiPrintDescPlancheNonNum: false,
                    bibliographieDansEstampesMultiplesDescriptionCitePage: "",
                    bibliographieDansEstampesMultiplesDescriptionListePage: "",
                    bibliographieDansEstampesMultiplesDescriptionSousNumero: "",
                    bibliographieDansEstampesMultiplesDescriptionPhotographe: "",
                    bibliographieDansEstampesMultiplesDescriptionReproductionCoul:
                      false,
                    biblioInMultiPrintDescReproCover: false,
                    bibliographieDansEstampesMultiplesDescriptionNonPagine: false,
                    bibliographieDansEstampesMultiplesDescriptionExemplaireRepr:
                      "",
                    bibliographieDansEstampesMultiplesDescriptionTexteLibre: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [fields, id, multipleprint, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsBibliographies}
      data={notRelatedBibliographies?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notRelatedBibliographies?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <BibliographyLinkGridCard
                  item={row.bibliography}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <MultipleprintInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="multipleprint"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) {
                      setSelectedRelationId(null);
                    }
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default MultipleprintInBibliographyLinks;
